/**
 * Actycrea: planescroll.esm.js (ES6)
 * MIT
 *
 * Scroll-driven plane showcase ( `module-plane-scroll` ). Sibling of
 * `module-plane`: same full-bleed stage, but the source is a self-hosted MP4
 * instead of a third-party Sketchfab embed, and the visitor advances it with
 * the scroll wheel only.
 *
 * INTERACTION MODEL — scroll only, never click. The media layer is inert
 * ( `pointer-events: none`, no `controls`, no play/pause affordance, no
 * keyboard focus ), so the only thing that moves the plane is the scroll
 * position. GSAP ScrollTrigger pins the section and maps the pinned scroll
 * range onto the video timeline ( scrub ), which is the same mechanism as
 * `galleryscroll.esm.js` — the page keeps behaving like a page, no wheel
 * hijacking and no scroll trap.
 *
 * WHY A BLOB — `currentTime = x` on a streamed video means a range request per
 * seek, and during a scrub that is dozens of requests per second: on 4G it
 * stalls. So the file is fetched once through a `ReadableStream` ( which also
 * gives a real progress figure for the loader ), turned into a `Blob` and
 * handed to the element as an object URL. From that point every seek is local
 * and instant, with zero network dependency while the visitor scrolls.
 *
 * ENCODE CONTRACT — the MP4 must be H.264 `yuv420p` with a SHORT GOP
 * ( `-g 10 -keyint_min 10 -sc_threshold 0`, `bframes=0:ref=1`, `+faststart` ).
 * A default web export uses GOP=250, which makes `currentTime` land up to 8
 * seconds away from the requested position and is the usual reason a video
 * scrub "feels broken". See the module analysis for the full benchmark.
 *
 * RENDER BRANCHES:
 * - `video`  — default. Blob + seek, confirmed with `requestVideoFrameCallback`
 *              when the browser exposes it.
 * - `frames` — plan B. A short WebP sequence painted on a canvas, driven by the
 *              very same ScrollTrigger. Used automatically when the video
 *              pipeline fails ( fetch/decode error ) and the layout provides a
 *              fallback gallery. Kept as the documented escape hatch for the
 *              iOS <= 16 seek reliability risk.
 * - `static` — no motion at all: the poster stays put and no pin is created.
 *              Used for `prefers-reduced-motion` and `save-data`.
 *
 * COMPLIANCE ( IBPA277 ): everything is self-hosted ( same origin, behind
 * Akamai ). No third party, no cookies, no personal data, so unlike
 * `module-plane` this module needs no OneTrust consent gating.
 *
 * @module esm/planescroll
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PREFIX } from '../helpers/config.js';

gsap.registerPlugin( ScrollTrigger );

const ATTR_ROOT     = `data-${PREFIX}-plane-scroll`;
const SELECTOR_ROOT = `[${ATTR_ROOT}]`;

const DEFAULTS = {
  // Pin length in viewport multiples. Overridden per instance by the ACF
  // `pin_length` field. 2 viewports over ~120 frames is roughly one frame
  // every 15 px of scroll, which reads as continuous motion.
  pinLength: 2 ,
  // Scrub smoothing ( seconds of catch-up ). Low enough that the plane still
  // feels attached to the wheel, high enough to absorb momentum scrolling.
  scrub: 0.5 ,
  // Viewport multiples of margin used to start the download ahead of time, so
  // the media is ready before the section reaches the top of the screen.
  preloadMargin: 1.5 ,
  // Assumed source frame rate. Only used as the seek dead band: deltas below
  // one frame are dropped instead of queueing a pointless seek.
  frameRate: 30 ,
  // Breakpoint ( px ) below which the mobile encode is preferred.
  mobileBreakpoint: 992
};

/**
 * One-time global ScrollTrigger setup guard.
 *
 * @type {boolean}
 */
let globalSetupDone = false;

/**
 * Global ScrollTrigger setup, applied once and only when at least one
 * plane-scroll module exists in the page. Mirrors `galleryscroll.esm.js`:
 *
 * - `normalizeScroll( true )` moves scroll handling onto requestAnimationFrame,
 *   which removes the inter-frame jumps that make a pin engage one frame late
 *   ( the perceived jolt right before a pinned section ).
 * - `refresh()` on window load, because main.js boots at DOMContentLoaded and
 *   lazy images can still change the document height afterwards, leaving the
 *   pin start position stale.
 *
 * @returns {void}
 */
function setupGlobal( ) {
  if ( globalSetupDone ) return;
  globalSetupDone = true;

  ScrollTrigger.normalizeScroll( true );

  if ( document.readyState === 'complete' ) {
    ScrollTrigger.refresh( );
  } else {
    window.addEventListener( 'load' , ( ) => ScrollTrigger.refresh( ) , { once: true } );
  }
}

/**
 * Whether the visitor asked for reduced motion.
 *
 * @returns {boolean}
 */
function prefersReducedMotion( ) {
  return !!( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches );
}

/**
 * Whether the visitor is on a metered / data-saving connection. Vendor-gated
 * API, so absence is treated as "no restriction".
 *
 * @returns {boolean}
 */
function prefersSaveData( ) {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return !!( connection && connection.saveData === true );
}

export class PlaneScroll {
  /**
   * @param {HTMLElement} element        - The module section root ( pin trigger ).
   * @param {Object}      [config = { }] - Overrides for DEFAULTS.
   */
  constructor( element , config = { } ) {
    if ( !( element instanceof HTMLElement ) ) throw new Error( 'PlaneScroll: elemento inválido' );

    this._element  = element;
    this._config   = { ...DEFAULTS , ...config };

    this._stage    = element.querySelector( `[${ATTR_ROOT}-stage]` );
    this._video    = element.querySelector( `[${ATTR_ROOT}-media]` );
    this._canvas   = element.querySelector( `[${ATTR_ROOT}-canvas]` );
    this._bar      = element.querySelector( `[${ATTR_ROOT}-bar]` );
    this._indicator = element.querySelector( `[${ATTR_ROOT}-indicator]` );

    this._mode      = 'static';
    this._trigger   = null;
    this._progress  = 0;
    this._painted   = -1;
    this._raf       = null;
    this._seeking   = false;
    this._pending   = false;
    this._duration  = 0;
    this._objectUrl = null;
    this._observer  = null;
    this._frames    = [ ];
    this._bitmaps   = [ ];
    this._context   = null;

    const pinLength = parseFloat( element.getAttribute( `${ATTR_ROOT}-pin` ) || '' );
    if ( pinLength > 0 ) {
      this._config.pinLength = pinLength;
    }

    this._sources = {
      desktop: element.getAttribute( `${ATTR_ROOT}-src` ) || '' ,
      mobile: element.getAttribute( `${ATTR_ROOT}-src-mobile` ) || ''
    };
    this._frames = this._parseFrames( element.getAttribute( `${ATTR_ROOT}-frames` ) );

    // No motion for this visitor: leave the poster in place and never build a
    // pin, so the page scrolls straight past the section.
    if ( prefersReducedMotion( ) || prefersSaveData( ) ) {
      this._element.classList.add( 'is-static' );
      return;
    }

    if ( !this._stage || !this._video || !this._source( ) ) {
      this._element.classList.add( 'is-static' );
      return;
    }

    this._watch( );
  }

  /**
   * Parses the JSON list of fallback frame URLs printed by the view.
   *
   * @param {string|null} raw - Raw attribute value.
   * @returns {Array<string>}
   */
  _parseFrames( raw ) {
    if ( !raw ) return [ ];
    try {
      const parsed = JSON.parse( raw );
      return Array.isArray( parsed ) ? parsed.filter( ( url ) => typeof url === 'string' && url !== '' ) : [ ];
    } catch ( error ) {
      return [ ];
    }
  }

  /**
   * Resolves the encode to download for the current viewport, falling back to
   * the desktop file when no mobile variant was provided.
   *
   * @returns {string}
   */
  _source( ) {
    const narrow = window.innerWidth < this._config.mobileBreakpoint;
    if ( narrow && this._sources.mobile ) {
      return this._sources.mobile;
    }
    return this._sources.desktop || this._sources.mobile;
  }

  /**
   * Defers the download until the section is within `preloadMargin` viewports,
   * so the module never competes with the hero for bandwidth and stays out of
   * the LCP critical path.
   *
   * @returns {void}
   */
  _watch( ) {
    if ( !( 'IntersectionObserver' in window ) ) {
      this._load( );
      return;
    }

    this._observer = new IntersectionObserver( ( entries ) => {
      entries.forEach( ( entry ) => {
        if ( !entry.isIntersecting ) return;
        this._observer.disconnect( );
        this._observer = null;
        this._load( );
      } );
    } , { rootMargin: `${this._config.preloadMargin * 100}% 0px` } );

    this._observer.observe( this._element );
  }

  /**
   * Downloads the encode as a `Blob` while reporting real progress, then hands
   * it to the element as an object URL. Falls back to a plain `Response.blob()`
   * when the body stream or the `Content-Length` header is unavailable.
   *
   * @returns {void}
   */
  _load( ) {
    this._element.classList.add( 'is-loading' );

    fetch( this._source( ) , { credentials: 'same-origin' } )
      .then( ( response ) => {
        if ( !response.ok ) {
          throw new Error( `PlaneScroll: HTTP ${response.status} al descargar el vídeo` );
        }
        return this._stream( response );
      } )
      .then( ( blob ) => this._attach( blob ) )
      .catch( ( error ) => this._fail( error ) );
  }

  /**
   * Reads the response body chunk by chunk to drive the loader bar, and
   * resolves with the assembled `Blob`.
   *
   * @param {Response} response - The fetch response.
   * @returns {Promise<Blob>}
   */
  _stream( response ) {
    const total = parseInt( response.headers.get( 'Content-Length' ) || '0' , 10 );

    if ( !response.body || !response.body.getReader || total <= 0 ) {
      return response.blob( );
    }

    const reader = response.body.getReader( );
    const chunks = [ ];
    let received = 0;

    const pump = ( ) => reader.read( ).then( ( { done , value } ) => {
      if ( done ) {
        return new Blob( chunks , { type: response.headers.get( 'Content-Type' ) || 'video/mp4' } );
      }
      chunks.push( value );
      received += value.length;
      this._progressBar( received / total );
      return pump( );
    } );

    return pump( );
  }

  /**
   * Paints the download progress bar.
   *
   * @param {number} ratio - Download ratio, 0 to 1.
   * @returns {void}
   */
  _progressBar( ratio ) {
    if ( !this._bar ) return;
    this._bar.style.transform = `scaleX(${Math.min( 1 , Math.max( 0 , ratio ) )})`;
  }

  /**
   * Attaches the downloaded blob to the video element and waits for metadata
   * before building the ScrollTrigger, so the pin is created against a known
   * duration.
   *
   * @param {Blob} blob - The downloaded encode.
   * @returns {void}
   */
  _attach( blob ) {
    this._objectUrl = URL.createObjectURL( blob );

    this._video.addEventListener( 'loadedmetadata' , ( ) => this._ready( ) , { once: true } );
    this._video.addEventListener( 'error' , ( ) => this._fail( new Error( 'PlaneScroll: el vídeo no se pudo decodificar' ) ) , { once: true } );
    this._video.addEventListener( 'seeked' , ( ) => this._seeked( ) );

    this._video.src = this._objectUrl;
    this._video.load( );
  }

  /**
   * Metadata is in: stores the duration, primes the decoder and starts the
   * scrub.
   *
   * @returns {void}
   */
  _ready( ) {
    this._duration = this._video.duration || 0;

    if ( !this._duration || !isFinite( this._duration ) ) {
      this._fail( new Error( 'PlaneScroll: duración del vídeo no disponible' ) );
      return;
    }

    this._mode = 'video';
    this._prime( );
    this._start( );
  }

  /**
   * Forces the first frame into the decoder. Safari does not reliably repaint a
   * paused video after a programmatic seek unless it has played at least once;
   * a muted, inline play/pause pair is enough and is silent to the visitor.
   *
   * @returns {void}
   */
  _prime( ) {
    try {
      const played = this._video.play( );
      if ( played && typeof played.then === 'function' ) {
        played.then( ( ) => this._video.pause( ) ).catch( ( ) => { /* Autoplay refused: the seek path still works. */ } );
      } else {
        this._video.pause( );
      }
    } catch ( error ) {
      /* Non-fatal: scrubbing does not depend on playback. */
    }
  }

  /**
   * Switches the module to the frames branch: preloads the WebP sequence and
   * paints it on the canvas with the same ScrollTrigger. Plan B for the
   * documented iOS seek reliability risk.
   *
   * @returns {void}
   */
  _frameBranch( ) {
    if ( !this._canvas || this._frames.length === 0 ) {
      this._element.classList.add( 'is-static' );
      return;
    }

    this._mode    = 'frames';
    this._context = this._canvas.getContext( '2d' );

    const total = this._frames.length;
    let loaded  = 0;

    const loaders = this._frames.map( ( url , index ) => new Promise( ( resolve ) => {
      const image = new Image( );
      image.decoding = 'async';
      image.onload = ( ) => {
        this._bitmaps[ index ] = image;
        loaded += 1;
        this._progressBar( loaded / total );
        resolve( );
      };
      image.onerror = ( ) => {
        loaded += 1;
        this._progressBar( loaded / total );
        resolve( );
      };
      image.src = url;
    } ) );

    Promise.all( loaders ).then( ( ) => {
      this._bitmaps = this._bitmaps.filter( ( bitmap ) => !!bitmap );
      if ( this._bitmaps.length === 0 ) {
        this._element.classList.add( 'is-static' );
        return;
      }
      this._sizeCanvas( );
      this._start( );
    } );
  }

  /**
   * Matches the canvas backing store to its rendered size, capped at 2x so the
   * buffer stays inside the memory limits of mobile Safari.
   *
   * @returns {void}
   */
  _sizeCanvas( ) {
    if ( !this._canvas ) return;

    const ratio = Math.min( 2 , window.devicePixelRatio || 1 );
    this._canvas.width  = Math.round( this._canvas.clientWidth * ratio );
    this._canvas.height = Math.round( this._canvas.clientHeight * ratio );
    this._painted = -1;
    this._schedule( );
  }

  /**
   * Builds the pinned ScrollTrigger. Both render branches share it: `onUpdate`
   * only stores the progress, and the actual seek or paint happens once per
   * animation frame in `_tick`.
   *
   * @returns {void}
   */
  _start( ) {
    this._element.classList.remove( 'is-loading' );
    this._element.classList.add( 'is-ready' );

    // A decode error can surface after the scrub is already live, which hands
    // over to the frames branch and lands here a second time. Kill the previous
    // instance so the section never ends up with two competing pins.
    if ( this._trigger ) {
      this._trigger.kill( );
      this._trigger = null;
    }

    this._trigger = ScrollTrigger.create( {
      trigger: this._element ,
      start: 'top top' ,
      end: ( ) => '+=' + ( window.innerHeight * this._config.pinLength ) ,
      scrub: this._config.scrub ,
      pin: true ,
      anticipatePin: 0 ,
      invalidateOnRefresh: true ,
      onUpdate: ( self ) => {
        this._progress = self.progress;
        this._indicate( self.progress );
        this._schedule( );
      } ,
      onRefresh: ( ) => this._sizeCanvas( )
    } );

    this._schedule( );
    setupGlobal( );
  }

  /**
   * Paints the scrub progress indicator.
   *
   * @param {number} ratio - Scrub ratio, 0 to 1.
   * @returns {void}
   */
  _indicate( ratio ) {
    if ( !this._indicator ) return;
    this._indicator.style.transform = `scaleX(${Math.min( 1 , Math.max( 0 , ratio ) )})`;
  }

  /**
   * Queues a single render for the next animation frame. Coalescing here is
   * what keeps fast wheel scrolling from piling up seeks the decoder would
   * then have to work through one by one.
   *
   * @returns {void}
   */
  _schedule( ) {
    if ( this._raf !== null ) return;
    this._raf = window.requestAnimationFrame( ( ) => {
      this._raf = null;
      this._render( );
    } );
  }

  /**
   * Renders the current progress through the active branch.
   *
   * @returns {void}
   */
  _render( ) {
    if ( this._mode === 'frames' ) {
      this._paint( );
      return;
    }
    this._seek( );
  }

  /**
   * Seeks the video to the current progress. Only one seek is in flight at a
   * time; anything requested while the decoder is busy is collapsed into a
   * single follow-up, so the visible frame always converges on the latest
   * scroll position instead of replaying the queue.
   *
   * @returns {void}
   */
  _seek( ) {
    if ( !this._duration ) return;

    if ( this._seeking ) {
      this._pending = true;
      return;
    }

    const step   = 1 / this._config.frameRate;
    const target = Math.min( this._duration - step , Math.max( 0 , this._progress * this._duration ) );

    if ( Math.abs( this._video.currentTime - target ) < step ) return;

    this._seeking = true;
    this._video.currentTime = target;

    // `requestVideoFrameCallback` fires once the new frame is actually
    // composited, which is a stronger signal than `seeked` and avoids
    // releasing the lock while the old frame is still on screen.
    if ( typeof this._video.requestVideoFrameCallback === 'function' ) {
      this._video.requestVideoFrameCallback( ( ) => this._seeked( ) );
    }
  }

  /**
   * Releases the seek lock and serves any request collapsed while it was held.
   *
   * @returns {void}
   */
  _seeked( ) {
    this._seeking = false;
    if ( this._pending ) {
      this._pending = false;
      this._schedule( );
    }
  }

  /**
   * Draws the frame matching the current progress on the canvas, covering the
   * stage like `object-fit: cover`.
   *
   * @returns {void}
   */
  _paint( ) {
    if ( !this._context || this._bitmaps.length === 0 ) return;

    const last  = this._bitmaps.length - 1;
    const index = Math.min( last , Math.max( 0 , Math.round( this._progress * last ) ) );

    if ( index === this._painted ) return;
    this._painted = index;

    const image  = this._bitmaps[ index ];
    const width  = this._canvas.width;
    const height = this._canvas.height;
    const scale  = Math.max( width / image.naturalWidth , height / image.naturalHeight );
    const drawW  = image.naturalWidth * scale;
    const drawH  = image.naturalHeight * scale;

    this._context.clearRect( 0 , 0 , width , height );
    this._context.drawImage( image , ( width - drawW ) / 2 , ( height - drawH ) / 2 , drawW , drawH );
  }

  /**
   * Handles a failed video pipeline: hands over to the frames branch when the
   * layout provides a fallback sequence, otherwise leaves the poster visible.
   * Either way no spinner is left running.
   *
   * @param {Error} error - The originating error.
   * @returns {void}
   */
  _fail( error ) {
    this._element.classList.remove( 'is-loading' , 'is-ready' );
    this._element.classList.add( 'is-error' );

    if ( window.console && console.warn ) console.warn( error );

    if ( this._mode !== 'frames' && this._frames.length > 0 ) {
      this._element.classList.add( 'is-loading' );
      this._frameBranch( );
    }
  }

  /**
   * Tears the instance down: kills the ScrollTrigger, drops the observer and
   * revokes the object URL so the blob can be garbage collected.
   *
   * @returns {void}
   */
  destroy( ) {
    if ( this._raf !== null ) {
      window.cancelAnimationFrame( this._raf );
      this._raf = null;
    }
    if ( this._observer ) {
      this._observer.disconnect( );
      this._observer = null;
    }
    if ( this._trigger ) {
      this._trigger.kill( );
      this._trigger = null;
    }
    if ( this._objectUrl ) {
      URL.revokeObjectURL( this._objectUrl );
      this._objectUrl = null;
    }
    this._bitmaps = [ ];
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ) {
    const nodes = root.querySelectorAll( SELECTOR_ROOT );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if ( el._planeScrollInstance ) return;
      el._planeScrollInstance = new PlaneScroll( el );
      instances.push( el._planeScrollInstance );
    } );
    return instances;
  }
}

export default PlaneScroll;
